import { format as formatDate } from "date-fns";
import { add, startOfYear } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { enUS } from "date-fns/locale/en-US";
import { tr } from "date-fns/locale/tr";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import React from "react";
import { months, weekDays } from "./CalendarYearViewConstants";

interface BaseEvent {
  periodStart: string;
  periodEnd: string;
}

export interface CalendarYearViewProps<T extends BaseEvent> {
  date: Date;
  events?: T[];
  renderEventDetails: (event: T) => React.ReactNode;
}

type DayEvent<T extends BaseEvent> = {
  event: T;
  isStart: boolean;
  isEnd: boolean;
};

const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const getEventsForDay = <T extends BaseEvent>(currentDate: Date, events: T[]): DayEvent<T>[] => {
  return events
    .filter((event) => {
      if (!event.periodStart || !event.periodEnd) return false;

      const eventStartDate = new Date(event.periodStart);
      const eventEndDate = new Date(event.periodEnd);

      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);

      if (
        // If the event is a single day event, check if the current date is within the event's start and end date
        isSameDay(eventStartDate, eventEndDate) &&
        startOfDay >= eventStartDate &&
        startOfDay <= eventEndDate
      ) {
        return true;
      }

      return startOfDay >= eventStartDate && startOfDay < eventEndDate;
    })
    .map((event) => ({
      event,
      isStart: isSameDay(new Date(event.periodStart), currentDate),
      isEnd: isSameDay(new Date(event.periodEnd), currentDate),
    }));
};

const EventIndicators = ({ count }: { count: number }) => (
  <div className="flex gap-0.5 mt-1">
    {Array.from({ length: Math.min(count, 3) }).map((_, index) => (
      <div key={index} className="w-1.5 h-1.5 rounded-full bg-primary" />
    ))}
    {count > 3 && <div className="w-1.5 h-1.5 rounded-full bg-primary opacity-50" />}
  </div>
);

const DayCell = <T extends BaseEvent>({
  day,
  date,
  events,
  renderEventDetails,
  i18n,
}: {
  day: number;
  date: Date;
  events: DayEvent<T>[];
  renderEventDetails: (event: T) => React.ReactNode;
  i18n: { language: string };
}) => {
  const currentDate = new Date(date.getFullYear(), date.getMonth(), day);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-14 w-full flex flex-col items-center justify-start p-1 relative",
            "hover:bg-accent hover:text-accent-foreground",
            new Date().toDateString() === currentDate.toDateString() && "bg-accent",
            events.length > 0 && "bg-primary/20",
            events.some((e) => e.isStart) && "border-l border-primary",
            events.some((e) => e.isEnd) && "border-r border-primary"
          )}
        >
          <span className="text-sm">{day}</span>
          {events.length > 0 && <EventIndicators count={events.length} />}
        </Button>
      </DialogTrigger>
      {events.length > 0 && (
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <div className="space-y-2">
            <h4 className="font-semibold">
              {formatDate(currentDate, "d MMMM yyyy", { locale: i18n.language === "tr" ? tr : enUS })}
            </h4>
            <div className="space-y-1">
              {events.map(({ event }, index) => (
                <div key={index} className="text-sm border-b pb-2">
                  {renderEventDetails(event)}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

const MonthView = <T extends BaseEvent>({ date, events = [], renderEventDetails }: CalendarYearViewProps<T>) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language.startsWith("tr") ? "tr" : "en";
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-2">{months[currentLang][date.getMonth()]}</h3>
      <div className="grid grid-cols-7 gap-1">
        {weekDays[currentLang].map((day) => (
          <div key={day} className="text-center text-sm text-muted-foreground">
            {day}
          </div>
        ))}
        {Array.from({ length: firstDay.getDay() }).map((_, i) => (
          <div key={`empty-${i}`} className="h-14" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
          const dayEvents = getEventsForDay(currentDate, events);

          return (
            <DayCell
              key={day}
              day={day}
              date={date}
              events={dayEvents}
              renderEventDetails={renderEventDetails}
              i18n={i18n}
            />
          );
        })}
      </div>
    </Card>
  );
};

export const CalendarYearView = <T extends BaseEvent>({
  date,
  events = [],
  renderEventDetails,
}: CalendarYearViewProps<T>) => {
  const months = [];
  const firstMonth = startOfYear(date);

  for (let i = 0; i < 12; i++) {
    const monthDate = add(firstMonth, { months: i });
    months.push(<MonthView key={i} date={monthDate} events={events} renderEventDetails={renderEventDetails} />);
  }

  return <div className="grid grid-cols-3 gap-4">{months}</div>;
};
